import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import '../styles/markdown.css';
import { useTranslation } from 'react-i18next';
import {
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  MagnifyingGlassPlusIcon,
  MagnifyingGlassMinusIcon,
  ArrowDownTrayIcon,
  DocumentMagnifyingGlassIcon,
  ArrowPathIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface PdfViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  pdfUrl: string;
  filename: string;
  onDownload?: () => void;
  ocrText?: string | null;
  ocrMarkdown?: string | null;
  ocrStatus?: string | null;
  ocrMethod?: string | null;
  onRetryOcr?: () => void;
  // Save a user-corrected transcription (grows the handwriting profile).
  onSaveCorrection?: (correctedMarkdown: string) => Promise<void>;
  // Refetch OCR state; used to poll while ocrStatus === 'processing'.
  onRefreshOcr?: () => Promise<void>;
}

export const PdfViewerModal: React.FC<PdfViewerModalProps> = ({
  isOpen,
  onClose,
  pdfUrl,
  filename,
  onDownload,
  ocrText,
  ocrMarkdown,
  ocrStatus,
  ocrMethod,
  onRetryOcr,
  onSaveCorrection,
  onRefreshOcr,
}) => {
  const { t } = useTranslation();
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [scale, setScale] = useState<number>(1.0);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showOcrText, setShowOcrText] = useState<boolean>(false);
  const [editing, setEditing] = useState<boolean>(false);
  const [draft, setDraft] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);
  const [savedFlash, setSavedFlash] = useState<boolean>(false);
  const [pollTimedOut, setPollTimedOut] = useState<boolean>(false);

  // The formatted transcription to show (Markdown preferred, plain text fallback).
  const displayMarkdown = ocrMarkdown || ocrText || '';

  // Keep the latest refresh callback in a ref so the polling effect can depend
  // only on [isOpen, ocrStatus] — otherwise the callback's changing identity
  // would reset the interval (and the timeout counter) on every poll.
  const refreshRef = useRef(onRefreshOcr);
  useEffect(() => { refreshRef.current = onRefreshOcr; }, [onRefreshOcr]);

  // Poll for completion while OCR is processing. Give up after ~90s and show a
  // "Re-run" prompt instead of an endless spinner (covers a stuck/orphaned job).
  const POLL_MS = 3000;
  const MAX_POLLS = 30; // ~90s
  useEffect(() => {
    if (!isOpen || ocrStatus !== 'processing') { setPollTimedOut(false); return; }
    setPollTimedOut(false);
    let count = 0;
    const id = setInterval(() => {
      count += 1;
      if (count > MAX_POLLS) {
        setPollTimedOut(true);
        clearInterval(id);
        return;
      }
      refreshRef.current?.();
    }, POLL_MS);
    return () => clearInterval(id);
  }, [isOpen, ocrStatus]);

  // Auto-open the transcription panel once results arrive.
  useEffect(() => {
    if (ocrStatus === 'completed' && displayMarkdown) setShowOcrText(true);
  }, [ocrStatus, displayMarkdown]);

  const startEditing = () => {
    setDraft(displayMarkdown);
    setEditing(true);
  };
  const cancelEditing = () => setEditing(false);
  const saveCorrection = async () => {
    if (!onSaveCorrection) return;
    setSaving(true);
    try {
      await onSaveCorrection(draft);
      setEditing(false);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2500);
    } catch {
      // keep editing so the user doesn't lose their edits
    } finally {
      setSaving(false);
    }
  };

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setPageNumber(1);
    setLoadError(null);
  };

  const onDocumentLoadError = () => {
    setLoadError('Failed to load PDF. Try downloading instead.');
  };

  const goToPrevPage = () => setPageNumber((prev) => Math.max(prev - 1, 1));
  const goToNextPage = () => setPageNumber((prev) => Math.min(prev + 1, numPages));
  const zoomIn = () => setScale((prev) => Math.min(prev + 0.25, 3.0));
  const zoomOut = () => setScale((prev) => Math.max(prev - 0.25, 0.5));

  const hasOcrContent = ocrStatus === 'completed' || ocrStatus === 'failed' || ocrStatus === 'processing';

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-60" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-4xl max-h-[90vh] bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 flex flex-col m-4">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-medium text-gray-900 dark:text-white truncate mr-4">
            {filename}
          </h3>
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Zoom controls */}
            <button
              onClick={zoomOut}
              className="p-1.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
              title="Zoom out"
            >
              <MagnifyingGlassMinusIcon className="h-5 w-5" />
            </button>
            <span className="text-xs text-gray-500 dark:text-gray-400 min-w-[3rem] text-center">
              {Math.round(scale * 100)}%
            </span>
            <button
              onClick={zoomIn}
              className="p-1.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
              title="Zoom in"
            >
              <MagnifyingGlassPlusIcon className="h-5 w-5" />
            </button>

            <div className="w-px h-5 bg-gray-300 dark:bg-gray-600 mx-1" />

            {/* Download */}
            {onDownload && (
              <button
                onClick={onDownload}
                className="p-1.5 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                title="Download"
              >
                <ArrowDownTrayIcon className="h-5 w-5" />
              </button>
            )}

            {/* Close */}
            <button
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
              title="Close"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* PDF Content */}
        <div className="flex-1 overflow-auto flex justify-center bg-gray-100 dark:bg-gray-900 p-4">
          {loadError ? (
            <div className="flex items-center justify-center text-sm text-red-600 dark:text-red-400">
              {loadError}
            </div>
          ) : (
            <Document
              file={pdfUrl}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
              loading={
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" />
                </div>
              }
            >
              <Page
                pageNumber={pageNumber}
                scale={scale}
                renderTextLayer={true}
                renderAnnotationLayer={true}
              />
            </Document>
          )}
        </div>

        {/* OCR Extracted Text Panel */}
        {hasOcrContent && (
          <div className="border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setShowOcrText(!showOcrText)}
              className="w-full px-4 py-2 flex items-center justify-between text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              <span className="flex items-center gap-2">
                <DocumentMagnifyingGlassIcon className="h-4 w-4" />
                {t('attachments.transcription', 'Transcription')}
                {ocrStatus === 'completed' && (
                  <span className="text-xs text-green-600 dark:text-green-400">
                    ({t('attachments.ocrComplete', 'OCR Complete')}{ocrMethod === 'manual' ? ' · edited' : ''})
                  </span>
                )}
                {ocrStatus === 'failed' && (
                  <span className="text-xs text-red-600 dark:text-red-400">
                    ({t('attachments.ocrFailed', 'OCR Failed')})
                  </span>
                )}
                {ocrStatus === 'processing' && (
                  <span className="text-xs text-amber-600 dark:text-amber-400">
                    ({t('attachments.ocrProcessing', 'Processing...')})
                  </span>
                )}
              </span>
              <ChevronDownIcon className={`h-4 w-4 transition-transform ${showOcrText ? 'rotate-180' : ''}`} />
            </button>
            {showOcrText && (
              <div className="bg-gray-50 dark:bg-gray-900">
                {/* Toolbar */}
                {(displayMarkdown || ocrStatus === 'failed') && (
                  <div className="flex items-center justify-end gap-2 px-4 pt-2">
                    {savedFlash && (
                      <span className="text-xs text-green-600 dark:text-green-400 mr-auto">
                        {t('attachments.correctionSaved', 'Saved — this helps future transcriptions.')}
                      </span>
                    )}
                    {!editing && displayMarkdown && onSaveCorrection && (
                      <button
                        onClick={startEditing}
                        className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                        title={t('attachments.editTranscription', 'Correct transcription')}
                      >
                        <PencilSquareIcon className="h-3.5 w-3.5" />
                        {t('attachments.correct', 'Correct')}
                      </button>
                    )}
                    {!editing && onRetryOcr && (
                      <button
                        onClick={onRetryOcr}
                        className="inline-flex items-center gap-1 text-xs text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300"
                        title={t('attachments.ocrRerun', 'Re-run OCR')}
                      >
                        <ArrowPathIcon className="h-3.5 w-3.5" />
                        {t('attachments.ocrRerun', 'Re-run')}
                      </button>
                    )}
                  </div>
                )}

                {/* Body */}
                <div className="px-4 py-3 max-h-72 overflow-auto">
                  {editing ? (
                    <div>
                      <textarea
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        rows={12}
                        className="w-full text-sm font-mono text-gray-800 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded p-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder={t('attachments.correctionPlaceholder', 'Fix any misread text (Markdown). Your corrections train better recognition of your handwriting over time.')}
                      />
                      <div className="flex items-center justify-end gap-2 mt-2">
                        <button
                          onClick={cancelEditing}
                          disabled={saving}
                          className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                        >
                          {t('actions.cancel', 'Cancel')}
                        </button>
                        <button
                          onClick={saveCorrection}
                          disabled={saving}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white rounded-lg"
                        >
                          {saving ? t('actions.saving', 'Saving…') : t('attachments.saveCorrection', 'Save correction')}
                        </button>
                      </div>
                    </div>
                  ) : ocrStatus === 'processing' ? (
                    pollTimedOut ? (
                      <div className="flex flex-col gap-2">
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {t('attachments.ocrSlow', 'Transcription is taking longer than expected. It may still finish — you can re-run it.')}
                        </span>
                        {onRetryOcr && (
                          <button
                            onClick={() => { setPollTimedOut(false); onRetryOcr(); }}
                            className="inline-flex items-center gap-1 self-start text-sm text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300"
                          >
                            <ArrowPathIcon className="h-3.5 w-3.5" />
                            {t('attachments.ocrRerun', 'Re-run')}
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600" />
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {t('attachments.ocrTranscribing', 'Transcribing your notes…')}
                        </span>
                      </div>
                    )
                  ) : displayMarkdown ? (
                    <div className="md-body text-gray-800 dark:text-gray-200">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{displayMarkdown}</ReactMarkdown>
                    </div>
                  ) : ocrStatus === 'failed' ? (
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {t('attachments.ocrFailed', 'Transcription failed. Use Re-run to try again.')}
                    </span>
                  ) : null}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Footer — Page Navigation */}
        {numPages > 0 && (
          <div className="flex items-center justify-center gap-4 px-4 py-2 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={goToPrevPage}
              disabled={pageNumber <= 1}
              className="p-1.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Page {pageNumber} of {numPages}
            </span>
            <button
              onClick={goToNextPage}
              disabled={pageNumber >= numPages}
              className="p-1.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
            >
              <ChevronRightIcon className="h-5 w-5" />
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};
